
import React from 'react';

const SmartVillagesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat" style={{backgroundImage: 'url(https://static.readdy.ai/image/46a311af9e0bece68b4ea3d810aabbdd/0878d8c143d820d898457c8bce554cfc.png)'}}>
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-green-900/60 to-blue-900/60"></div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-full px-10 py-5 inline-block mb-6">
              <span className="text-white font-semibold text-xl">Smart Villages<sup>TM</sup> South Africa</span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Rural Transformation
              <span className="text-green-400"> Through Innovation</span>
              <br />
              Where <span className="text-blue-400">Communities Flourish</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Smart Villages transform under-serviced communal land into thriving, income-generating communities—combining modern infrastructure, local enterprise, and traditional governance so rural areas can prosper on their own terms—creating opportunities for households, SMMEs, and strategic partners.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a 
                href="/contact" 
                className="bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center text-lg"
              >
                <i className="ri-rocket-line mr-2"></i>
                Join the Transformation
              </a>
              <a 
                href="#vision" 
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white hover:text-gray-900 px-8 py-4 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                Learn More
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <i className="ri-wifi-line text-white text-2xl mb-2 block"></i>
                <span className="text-white font-medium">Fiber Internet</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <i className="ri-leaf-line text-white text-2xl mb-2 block"></i>
                <span className="text-white font-medium">Clean Energy</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                <i className="ri-community-line text-white text-2xl mb-2 block"></i>
                <span className="text-white font-medium">Vibrant Community</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="animate-bounce">
            <i className="ri-arrow-down-line text-white text-2xl"></i>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="py-20 bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-6 animate-pulse">
              🚀 A Revolutionary Way to Live
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Why Smart Villages Will
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent"> Change Everything</span>
            </h2>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 mb-12 shadow-lg border border-white/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <i className="ri-lightbulb-flash-line text-yellow-500 mr-3 text-3xl"></i>
                    The Game-Changing Formula
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Smart Villages fix the core blockers of rural South Africa in <strong>one coordinated move</strong>: services, jobs, and investable structure on communal land.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-fill text-green-500 mr-3"></i>
                      <span className="text-gray-700">Bundle infrastructure with economic opportunity</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-fill text-blue-500 mr-3"></i>
                      <span className="text-gray-700">Stop the exodus of people and money to cities</span>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-checkbox-circle-fill text-purple-500 mr-3"></i>
                      <span className="text-gray-700">Create self-running rural economies</span>
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://static.readdy.ai/image/46a311af9e0bece68b4ea3d810aabbdd/0878d8c143d820d898457c8bce554cfc.png" 
                    alt="Smart Village transformation in action" 
                    className="rounded-xl shadow-lg object-cover w-full h-64"
                  />
                  <div className="absolute top-4 right-4 bg-green-600/90 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ✨ The Future is Now
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Transformation Process */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">The Transformation Process</h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Watch how Smart Villages create a domino effect of positive change
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="relative bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-gray-200 transition-all duration-500 cursor-pointer">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300">
                    <i className="ri-water-percent-line text-white text-2xl"></i>
                  </div>
                  <div className="text-sm text-gray-500 mb-1">Step 1</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Infrastructure First</h4>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Clean water, solar power, fiber internet - the basics that unlock everything else
                  </p>
                  <div className="text-2xl font-bold text-blue-600 mb-1">100%</div>
                  <div className="text-xs text-gray-500">Service Coverage</div>
                </div>
              </div>

              <div className="relative bg-white rounded-xl p-6 shadow-lg border-2 border-green-200 transform scale-105">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 scale-110">
                    <i className="ri-briefcase-line text-white text-2xl"></i>
                  </div>
                  <div className="text-sm text-gray-500 mb-1">Step 2</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Jobs Follow</h4>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Co-ops, skills training, and local enterprise hubs create sustainable employment
                  </p>
                  <div className="text-2xl font-bold text-green-600 mb-1">300+</div>
                  <div className="text-xs text-gray-500">Jobs Created</div>
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                  <i className="ri-play-fill text-white text-sm"></i>
                </div>
              </div>

              <div className="relative bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-gray-200 transition-all duration-500 cursor-pointer">
                <div className="text-center">
                  <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300">
                    <i className="ri-home-heart-line text-white text-2xl"></i>
                  </div>
                  <div className="text-sm text-gray-500 mb-1">Step 3</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Wealth Builds</h4>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Land ownership and thriving local economy means money stays and grows
                  </p>
                  <div className="text-2xl font-bold text-yellow-600 mb-1">Together</div>
                  <div className="text-xs text-gray-500">Community grows</div>
                </div>
              </div>

              <div className="relative bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-gray-200 transition-all duration-500 cursor-pointer">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300">
                    <i className="ri-community-line text-white text-2xl"></i>
                  </div>
                  <div className="text-sm text-gray-500 mb-1">Step 4</div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">Communities Thrive</h4>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Young people stay, families prosper, traditions live alongside innovation
                  </p>
                  <div className="text-2xl font-bold text-purple-600 mb-1">∞</div>
                  <div className="text-xs text-gray-500">Generational Impact</div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/46a311af9e0bece68b4ea3d810aabbdd/100afd100be2d8258757c2ab3f44192d.png" 
                alt="Modern rural living in Smart Villages" 
                className="rounded-lg shadow-xl object-cover w-full h-80"
              />
              <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                Your New Reality
              </div>
            </div>
            <div className="space-y-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-heart-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Own Real Land, Build Real Wealth</h3>
                    <p className="text-gray-600">
                      From R68,000, own 900m² of serviced land. No monthly rent. No landlords. Your investment grows as the community thrives.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-wifi-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Work From Paradise</h3>
                    <p className="text-gray-600">
                      Fiber internet faster than most cities. Solar power that never fails. Work remotely while your children play safely outside.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-community-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Community That Cares</h3>
                    <p className="text-gray-600">
                      Neighbors who become family. Shared gardens, co-working spaces, and cultural centers where traditions live alongside innovation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qokolweni Pilot Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-medium mb-6">
              🏡 Now Building in Eastern Cape
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Qokolweni: Your New Home is Ready
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stop dreaming about the perfect place to live. Qokolweni is here, happening now, with people already building their dream homes. Your stand is waiting.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/46a311af9e0bece68b4ea3d810aabbdd/de44de059fc3956bbd8cf61733564ffa.png" 
                alt="Qokolweni Smart Village development in progress - your future home" 
                className="rounded-lg shadow-xl object-cover w-full h-80"
              />
              <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
                🔨 Construction Active
              </div>
              <div className="absolute bottom-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                Phase 1 Available
              </div>
            </div>

            <div>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Real Location, Real Progress</h3>
                    <p className="text-gray-600 text-sm">Eastern Cape, South Africa</p>
                  </div>
                </div>
                <blockquote className="text-lg text-gray-800 mb-4 italic">
                  "Africa holds vast reserves of rural land. Transformative developments like these unlock that potential by turning underutilized spaces into vibrant communities, delivering the infrastructure and opportunities so urgently needed."
                </blockquote>
                <cite className="text-sm text-green-600 font-semibold">— HRH Nkosi Vulithuba Sangoni</cite>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3 p-4 bg-white border border-green-200 rounded-lg">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Infrastructure Complete:</span>
                    <span className="text-gray-600 ml-2">Roads, water, electricity ready</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white border border-blue-200 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-line text-blue-600"></i>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Families Moving In:</span>
                    <span className="text-gray-600 ml-2">Real people, real homes, real community</span>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white border border-yellow-200 rounded-lg">
                  <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-yellow-600"></i>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">Limited Availability:</span>
                    <span className="text-gray-600 ml-2">Only 121 stands remaining in Phase 1</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h4 className="font-bold text-gray-900 mb-3">What You Get for R68,000:</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                    600-1000m² fully serviced land
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                    Water, electricity, and sewerage connections
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                    Road access and street lighting
                  </li>
                  <li className="flex items-center">
                    <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                    Community center and shared facilities
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="/contact" 
                  className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-heart-line mr-2"></i>
                  Buy Your Stand Today
                </a>
                <a 
                  href="/contact" 
                  className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Tour Qokolweni
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Opportunities */}
      <section className="py-20 bg-gradient-to-r from-gray-900 via-green-800 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-yellow-500 text-gray-900 px-6 py-2 rounded-full text-sm font-bold mb-6 animate-pulse">
              ⚡ Limited Opportunity: Phase 1 Transformation Underway
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Rural Transformation Journey Starts Here
            </h2>
            <p className="text-xl text-green-100 mb-12 max-w-3xl mx-auto">
              Stop waiting for change to happen. Be part of the rural transformation that's reshaping South Africa. Qokolweni is real, it's happening now, and your opportunity to contribute is here. But spaces are limited.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border-2 border-transparent hover:border-green-400 transition-all group">
                <div className="w-16 h-16 bg-green-600 group-hover:bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <i className="ri-home-heart-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Invest in Community</h3>
                <p className="text-green-100 mb-4">
                  From R68,000, secure 600-1000m² in Africa's most innovative rural community. Build wealth while building communities.
                </p>
                <div className="text-2xl font-bold text-yellow-400 mb-4">From R68,000</div>
                <a 
                  href="/contact" 
                  className="inline-block bg-green-500 hover:bg-green-400 text-white px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Explore Opportunities
                </a>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border-2 border-transparent hover:border-blue-400 transition-all group">
                <div className="w-16 h-16 bg-blue-600 group-hover:bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <i className="ri-eye-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Experience the Vision</h3>
                <p className="text-blue-100 mb-4">
                  Visit Qokolweni today. Walk the transformation. Meet the community. Experience what rural innovation looks like.
                </p>
                <div className="text-lg font-semibold text-white mb-4">Eastern Cape, SA</div>
                <a 
                  href="/contact" 
                  className="inline-block bg-blue-500 hover:bg-blue-400 text-white px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Schedule Your Visit
                </a>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center border-2 border-transparent hover:border-yellow-400 transition-all group">
                <div className="w-16 h-16 bg-yellow-600 group-hover:bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <i className="ri-rocket-line text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">Scale the Impact</h3>
                <p className="text-yellow-100 mb-4">
                  Partner with us to bring this transformation to every corner of Africa. Strategic investment and collaboration opportunities available.
                </p>
                <div className="text-lg font-semibold text-white mb-4">Multiple Partnership Levels</div>
                <a 
                  href="/contact" 
                  className="inline-block bg-yellow-500 hover:bg-yellow-400 text-gray-900 px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  Partner With Us
                </a>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 max-w-4xl mx-auto">
              <div className="flex items-center justify-center mb-6">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-alarm-warning-line text-white text-xl"></i>
                </div>
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white">Only 121 Investment Opportunities Left in Phase 1</h3>
                  <p className="text-red-200">Next phase investment threshold increases by 25%</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-white mb-6 leading-relaxed">
                  Stay informed about transformation progress, investment opportunities, partnership updates, and community development milestones. Join 2,847 forward-thinking individuals already part of the rural transformation movement.
                </p>
              </div>
              <div className="flex justify-center">
                <a 
                  href="/contact" 
                  className="bg-green-500 hover:bg-green-400 text-white px-8 py-4 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center text-lg"
                >
                  <i className="ri-mail-line mr-2"></i>
                  Join the Transformation Updates
                </a>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-300 mb-2">Ready to talk? Call us now:</p>
              <div className="text-2xl font-bold text-white">
                <i className="ri-phone-line mr-2"></i>
                (+27) 79 413 9048
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmartVillagesPage;
